/**
 * NewTaskDialog — shared "create task" modal.
 *
 * Used by:
 *   - Tasks page (top-right "New Task" button)
 *   - Contact detail page (Tasks & Follow-ups sidebar "Add New Task")
 *   - Deal detail page (future)
 *
 * Accepts optional defaults to pre-scope the task to a contact/deal.
 */
import { useEffect, useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Loader2, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Combobox } from "@/components/ui/combobox"
import {
  tasksApi,
  usersApi,
  contactsApi,
  dealsApi,
  type Priority,
  type TaskInput,
} from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import { cn } from "@/lib/utils"

// -------- date helpers ---------
// All task dates are ALWAYS Brisbane wall-clock time — Rivercity is a
// Brisbane-based brokerage, so a broker in another tz still sees + sets
// "5pm" as 5pm Brisbane, never 5pm their local tz.
const BRIS_TZ = "Australia/Brisbane"
function brisParts(d: Date): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: BRIS_TZ,
  }).formatToParts(d)
  const num = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0")
  const rawHour = num("hour")
  return {
    year: num("year"),
    month: num("month"),
    day: num("day"),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: num("minute"),
  }
}
// Encode a Brisbane wall-clock instant into a Date. Brisbane is fixed
// UTC+10 (no DST in QLD), so wall-clock "HH:MM on date" corresponds to
// UTC "HH-10:MM on same date".
function brisWallToDate(year: number, month: number, day: number, hour: number, minute: number): Date {
  return new Date(Date.UTC(year, month - 1, day, hour - 10, minute, 0, 0))
}
// Render a Brisbane wall-clock string for <input type="datetime-local">.
function toLocalInputValue(d: Date): string {
  const p = brisParts(d)
  const pad = (n: number) => String(n).padStart(2, "0")
  return (
    `${p.year}-${pad(p.month)}-${pad(p.day)}` +
    `T${pad(p.hour)}:${pad(p.minute)}`
  )
}
// Parse the string emitted by <input type="datetime-local"> as Brisbane
// wall-clock time, not the user's browser-local tz.
function parseLocalInputValue(v: string): Date | null {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!m) return null
  return brisWallToDate(
    Number(m[1]),
    Number(m[2]),
    Number(m[3]),
    Number(m[4]),
    Number(m[5])
  )
}
// Build a Brisbane wall-clock date offset from "now": today+deltaDays at
// hour:minute Brisbane.
function brisRelative(deltaDays: number, hour: number, minute = 0): Date {
  const today = brisParts(new Date())
  // Compose a Date at today Brisbane midnight, then step days via
  // millisecond arithmetic (safe because Brisbane has no DST).
  const base = brisWallToDate(today.year, today.month, today.day, hour, minute)
  return new Date(base.getTime() + deltaDays * 24 * 60 * 60 * 1000)
}
function defaultDueDate() {
  return brisRelative(1, 17, 0)
}
function dueTodayEOD() {
  return brisRelative(0, 17, 0)
}
function dueNextWeek() {
  return brisRelative(7, 17, 0)
}

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
]

export type NewTaskDialogProps = {
  open: boolean
  onOpenChange: (o: boolean) => void
  /** Pre-select a contact and lock the picker to just their deals. */
  defaultContactId?: string | null
  /** Pre-select a deal (contact will be inferred if the contact is not set). */
  defaultDealId?: string | null
  /**
   * If provided, the dialog runs in EDIT mode: pre-fills form from this task
   * and PATCHes on submit instead of POSTing. Pass `null` for create mode.
   */
  editTask?: {
    id: string
    title: string
    description: string | null
    priority: Priority
    status: "pending" | "completed" | "cancelled"
    dueAt: number | null
    ownerId: string | null
    contactId: string | null
    dealId: string | null
  } | null
  /** Called after the task is created / updated, before the dialog closes. */
  onCreated?: (taskId: string) => void
}

export function NewTaskDialog({
  open,
  onOpenChange,
  defaultContactId = null,
  defaultDealId = null,
  editTask = null,
  onCreated,
}: NewTaskDialogProps) {
  const qc = useQueryClient()
  const { user } = useAuth()

  const isEdit = !!editTask

  const emptyForm = (): TaskInput => {
    if (editTask) {
      return {
        title: editTask.title,
        description: editTask.description,
        priority: editTask.priority,
        status: editTask.status,
        dueAt: editTask.dueAt,
        ownerId: editTask.ownerId,
        contactId: editTask.contactId,
        dealId: editTask.dealId,
      }
    }
    return {
      title: "",
      description: null,
      priority: "medium",
      status: "pending",
      dueAt: Math.floor(defaultDueDate().getTime() / 1000),
      ownerId: user?.id ?? null,
      contactId: defaultContactId,
      dealId: defaultDealId,
    }
  }

  const [form, setForm] = useState<TaskInput>(emptyForm)
  const [error, setError] = useState<string | null>(null)

  // Reset when opened (or when pre-selected context changes)
  useEffect(() => {
    if (open) {
      setForm(emptyForm())
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, defaultContactId, defaultDealId, editTask?.id])

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
    enabled: open,
  })
  const contactList = useQuery({
    queryKey: ["contacts-picker"],
    queryFn: () => contactsApi.list({ pageSize: 100 }),
    enabled: open,
  })
  const dealList = useQuery({
    queryKey: ["deals-picker", form.contactId],
    queryFn: () =>
      dealsApi.list(
        form.contactId ? { contactId: form.contactId, pageSize: 100 } : { pageSize: 100 }
      ),
    enabled: open,
  })

  const create = useMutation({
    mutationFn: (data: TaskInput) => tasksApi.create(data),
    onSuccess: (r) => {
      invalidateAll()
      onCreated?.(r.id)
      onOpenChange(false)
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Failed to create task"),
  })

  const update = useMutation({
    mutationFn: (data: TaskInput) =>
      tasksApi.update(editTask!.id, {
        title: data.title,
        description: data.description,
        priority: data.priority,
        status: data.status,
        dueAt: data.dueAt,
        ownerId: data.ownerId,
        contactId: data.contactId,
        dealId: data.dealId,
      }),
    onSuccess: () => {
      invalidateAll()
      if (editTask) onCreated?.(editTask.id)
      onOpenChange(false)
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Failed to update task"),
  })

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ["tasks"] })
    qc.invalidateQueries({ queryKey: ["tasks-counts"] })
    qc.invalidateQueries({ queryKey: ["contact"] })
    qc.invalidateQueries({ queryKey: ["contact-tasks"] })
  }

  const contactOptions = useMemo(
    () =>
      (contactList.data?.items ?? []).map((c) => ({
        value: c.id,
        label: c.businessName ?? (`${c.firstName} ${c.lastName}`.trim() || "(no name)"),
        sublabel: c.email ?? c.mobile ?? undefined,
      })),
    [contactList.data]
  )
  const dealOptions = useMemo(
    () =>
      (dealList.data?.items ?? []).map((d) => ({
        value: d.id,
        label: d.dealName ?? "Untitled deal",
        sublabel:
          d.loanAmount != null ? `$${d.loanAmount.toLocaleString("en-AU")}` : d.pipelineStage,
      })),
    [dealList.data]
  )
  const brokerOptions = useMemo(
    () =>
      (brokers.data?.users ?? []).map((u) => ({
        value: u.id,
        label: u.fullName,
        sublabel: u.email,
      })),
    [brokers.data]
  )

  const dueDate = form.dueAt ? new Date(form.dueAt * 1000) : null
  const dueValue = dueDate ? toLocalInputValue(dueDate) : ""
  const setDuePreset = (d: Date) =>
    setForm((f) => ({ ...f, dueAt: Math.floor(d.getTime() / 1000) }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit task" : "New task"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            if (!form.title.trim()) {
              setError("Give the task a title")
              return
            }
            if (isEdit) update.mutate(form)
            else create.mutate(form)
          }}
          className="space-y-5"
        >
          {error && (
            <div className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-2.5 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="task-title">
              What needs doing? <span className="text-destructive">*</span>
            </Label>
            <Input
              id="task-title"
              required
              autoFocus
              placeholder="e.g. Call John about settlement docs"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="task-desc">
              Notes <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Textarea
              id="task-desc"
              rows={2}
              placeholder="Extra context, links, etc."
              value={form.description ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value || null }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="task-due">Due</Label>
              <Input
                id="task-due"
                type="datetime-local"
                value={dueValue}
                onChange={(e) => {
                  const v = e.target.value
                  const parsed = v ? parseLocalInputValue(v) : null
                  setForm((f) => ({
                    ...f,
                    dueAt: parsed ? Math.floor(parsed.getTime() / 1000) : null,
                  }))
                }}
              />
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setDuePreset(dueTodayEOD())}
                  className="text-rc-blue-700 hover:bg-rc-blue-50 rounded px-2 py-0.5 text-xs"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => setDuePreset(defaultDueDate())}
                  className="text-rc-blue-700 hover:bg-rc-blue-50 rounded px-2 py-0.5 text-xs"
                >
                  Tomorrow
                </button>
                <button
                  type="button"
                  onClick={() => setDuePreset(dueNextWeek())}
                  className="text-rc-blue-700 hover:bg-rc-blue-50 rounded px-2 py-0.5 text-xs"
                >
                  In a week
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, dueAt: null }))}
                  className="text-muted-foreground hover:text-foreground rounded px-2 py-0.5 text-xs"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <div className="grid grid-cols-3 gap-1">
                {PRIORITY_OPTIONS.map((opt) => {
                  const active = form.priority === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, priority: opt.value }))}
                      className={cn(
                        "rounded-md border px-2 py-2 text-xs font-medium transition-colors",
                        active
                          ? opt.value === "high"
                            ? "border-destructive bg-destructive/10 text-destructive"
                            : opt.value === "low"
                              ? "border-rc-grey-300 bg-rc-grey-50 text-foreground"
                              : "border-rc-blue-500 bg-rc-blue-50 text-rc-blue-700"
                          : "border-border bg-background text-muted-foreground hover:border-rc-blue-300"
                      )}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Contact</Label>
              <Combobox
                options={contactOptions}
                value={form.contactId ?? null}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    contactId: v,
                    dealId: v ? f.dealId ?? null : null,
                  }))
                }
                placeholder="Search contacts…"
                loading={contactList.isLoading}
                disabled={!!defaultContactId}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Deal</Label>
              <Combobox
                options={dealOptions}
                value={form.dealId ?? null}
                onChange={(v) => setForm((f) => ({ ...f, dealId: v }))}
                placeholder={form.contactId ? "Search deals…" : "Any deal…"}
                loading={dealList.isLoading}
                emptyText={form.contactId ? "No deals for this contact" : "No deals found"}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Assign to</Label>
            <Combobox
              options={brokerOptions}
              value={form.ownerId ?? null}
              onChange={(v) => setForm((f) => ({ ...f, ownerId: v }))}
              placeholder="Pick a broker…"
              loading={brokers.isLoading}
              clearable={false}
            />
            <p className="text-muted-foreground text-xs">
              They'll get a bell notification when the task is created.
            </p>
          </div>

          <div className="border-border flex items-center justify-between gap-2 border-t pt-3">
            <div className="text-muted-foreground text-xs">
              {form.dueAt ? (
                <>
                  Due{" "}
                  {new Date(form.dueAt * 1000).toLocaleString("en-AU", {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone: BRIS_TZ,
                  })}
                </>
              ) : (
                <>No due date</>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {(create.isPending || update.isPending) && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                )}
                {isEdit ? "Save changes" : "Create task"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
